import { useNetworkElementsStore } from '../store/networkElementsStore';
import { useCouplingStore } from '../store/couplingStore';
import type { CouplingFlow } from '../types/coupling';
import { PU_BASE_LN_V } from '../utils/electricalConstants';
import { parseUtcTimestampMs } from '../utils/timeUtils';
import { parseLoadInputSource } from '../utils/meterProvenance';

export interface RingGroupResponse {
  elementIds: string[];
  ringId: string;
}

export interface PowerCalculationResponse {
  ringGroups?: RingGroupResponse[] | null;
  /** Trennstellen figures from the owner zones' results (live only; archive frames carry none). */
  couplingFlows?: CouplingFlow[] | null;
  measurements: Array<{
    element_id: string;
    element_type: string;
    value: number;
    timestamp: string;
    simulated: number | null;
    loading_percent: number | null;
    // Voltage measurements
    voltage_kv?: number;
    voltage_pu?: number;
    measured_voltage_v?: number;
    measured_voltage_pu?: number;
    measured_voltage_reference?: string;
    measured_voltage_source?: string;
    measured_voltage_timestamp?: string;
    // Power measurements for lines
    power_kva?: number;
    power_kvar?: number;
    power_kw?: number;
    current_a?: number;
    current_from_a?: number;
    current_to_a?: number;
    // Per-phase currents for 3-phase display
    current_l1_a?: number;
    current_l2_a?: number;
    current_l3_a?: number;
    // Bus voltage measurements for lines
    from_bus_voltage_pu?: number;
    to_bus_voltage_pu?: number;
    from_bus_voltage_kv?: number;
    to_bus_voltage_kv?: number;
    // Three-phase voltage data
    voltage_l1_pu?: number;
    voltage_l2_pu?: number;
    voltage_l3_pu?: number;
    voltage_unbalance_percent?: number;
    // Three-phase power data
    power_l1_kw?: number;
    power_l2_kw?: number;
    power_l3_kw?: number;
    // Unit indicator
    unit?: string;
    // Hausanschluss: origin of the load-flow input (measured / outlet_balance / load_profile / annual_average)
    input_source?: string;
  }>;
}

/**
 * Maps a /powercalculation-shaped response into the networkElementsStore
 * (powerMeasurements + segments + ring-circuit flags on Abgangsleitungen).
 *
 * Shared by the live 10s poll (powerFlowService) and the historical playback
 * (historicalPlaybackStore) — the backend serves historical snapshots in the
 * exact same wire shape, so live and rewind render through one code path.
 */
export function applyPowerCalculationResponse(data: PowerCalculationResponse): void {
  const measurements = data && Array.isArray(data.measurements) ? data.measurements : [];
  const mapped: Record<string, any> = {};
  const store = useNetworkElementsStore.getState();
  // Only measurements of elements in this zone: the store must not grow with foreign ids
  // (a 17 MB all-zones payload was observed on VER - 80k measurements mapped, 118 MB heap).
  const knownIds = new Set<string>();
  for (const el of store.elements) knownIds.add(el.id);
  const belongsToZone = (id: string) => knownIds.has(id) || knownIds.has(id.split('_seg')[0]);
  // Segment updates are collected and written in ONE store update at the end (see
  // updateSegments) - one write per segment was O(N^2) and N notifications per poll.
  const segmentUpdates: Record<string, Parameters<typeof store.updateSegment>[1]> = {};

  for (const m of measurements) {
    if (!m.element_id) continue;
    if (knownIds.size > 0 && !belongsToZone(m.element_id)) continue;
    const voltageKv = typeof m.voltage_kv === 'number' ? m.voltage_kv : null;
    const voltagePu = typeof m.voltage_pu === 'number' ? m.voltage_pu : null;
    const voltageV = voltageKv !== null ? voltageKv * 1000 : null;
    const measuredVoltageV = typeof m.measured_voltage_v === 'number' ? m.measured_voltage_v : null;
    const measuredVoltagePu = typeof m.measured_voltage_pu === 'number' ? m.measured_voltage_pu : null;
    const measuredVoltageReference = typeof m.measured_voltage_reference === 'string' ? m.measured_voltage_reference : null;
    const measuredVoltageSource = typeof m.measured_voltage_source === 'string' ? m.measured_voltage_source : null;
    const measuredVoltageTimestamp = typeof m.measured_voltage_timestamp === 'string' ? m.measured_voltage_timestamp : null;

    // Handle line measurements differently
    if (m.element_type === 'line') {
      const powerKva = typeof m.power_kva === 'number' ? m.power_kva : null;
      const powerKvar = typeof m.power_kvar === 'number' ? m.power_kvar : null;
      const powerKw = typeof m.power_kw === 'number' ? m.power_kw : null;
      const lineCurrentA = typeof m.current_a === 'number' ? m.current_a : null;

      // Per-phase values: prefer the solver's real per-phase currents. In a
      // balanced system the per-phase current EQUALS the line current (never
      // current/3), and the per-phase reference voltage is L-N (~230.94 V),
      // not the 400 V L-L value.
      const linePhases = {
        L1: { voltage: PU_BASE_LN_V, current: m.current_l1_a ?? lineCurrentA ?? 0, power: (powerKw ?? 0) / 3 },
        L2: { voltage: PU_BASE_LN_V, current: m.current_l2_a ?? lineCurrentA ?? 0, power: (powerKw ?? 0) / 3 },
        L3: { voltage: PU_BASE_LN_V, current: m.current_l3_a ?? lineCurrentA ?? 0, power: (powerKw ?? 0) / 3 }
      };

      // Create measurement for the line element itself
      mapped[m.element_id] = {
        totalActivePower: powerKw ?? 0,
        timestamp: parseUtcTimestampMs(m.timestamp),
        simValue: m.simulated ?? null,
        resultValue: powerKva ?? powerKw ?? 0, // Prefer apparent power for lines
        phases: linePhases,
        voltageKV: voltageKv,
        voltagePU: voltagePu,
        measuredVoltageV,
        measuredVoltagePu,
        measuredVoltageReference,
        measuredVoltageSource,
        measuredVoltageTimestamp
      };

      // Segment data with simplified power flow data (written in one batch below)
      segmentUpdates[m.element_id] = {
        id: m.element_id,
        powerKW: powerKw ?? 0,
        powerKVAR: powerKvar ?? 0,
        powerKVA: powerKva ?? null,
        currentA: lineCurrentA ?? 0,
        currentFromA: typeof m.current_from_a === 'number' ? m.current_from_a : null,
        currentToA: typeof m.current_to_a === 'number' ? m.current_to_a : null,
        // Per-phase currents for 3-phase display
        currentL1A: typeof m.current_l1_a === 'number' ? m.current_l1_a : null,
        currentL2A: typeof m.current_l2_a === 'number' ? m.current_l2_a : null,
        currentL3A: typeof m.current_l3_a === 'number' ? m.current_l3_a : null,
        loadingPercent: m.loading_percent ?? null, // Use backend-calculated loading percent
        name: `Segment ${m.element_id}`,
        sourceElement: null,
        targetElement: null,
        length: 0, // Length should come from element definition
        fromBusVoltagePu: typeof m.from_bus_voltage_pu === 'number' ? m.from_bus_voltage_pu : null,
        toBusVoltagePu: typeof m.to_bus_voltage_pu === 'number' ? m.to_bus_voltage_pu : null,
        fromBusVoltageKv: typeof m.from_bus_voltage_kv === 'number' ? m.from_bus_voltage_kv : null,
        toBusVoltageKv: typeof m.to_bus_voltage_kv === 'number' ? m.to_bus_voltage_kv : null,
        calculatedAt: parseUtcTimestampMs(m.timestamp),
      };

      // Also update parent Abgangsleitung
      const parentId = m.element_id.split('_seg')[0];
      if (parentId !== m.element_id) {
        mapped[parentId] = {
          totalActivePower: powerKw ?? 0,
          timestamp: parseUtcTimestampMs(m.timestamp),
          simValue: m.simulated ?? null,
          resultValue: powerKva ?? powerKw ?? 0,
          phases: linePhases,
          voltageKV: voltageKv,
          voltagePU: voltagePu,
          measuredVoltageV,
          measuredVoltagePu,
          measuredVoltageReference,
          measuredVoltageSource,
          measuredVoltageTimestamp
        };
      }
    } else {
      // Handle non-line measurements (hausanschluss, trafostation, etc.)
      const measurementValue = m.unit === 'kV' ? 0 : (m.value ?? 0);

      // Extract three-phase voltage data
      const voltageL1Pu = typeof m.voltage_l1_pu === 'number' ? m.voltage_l1_pu : null;
      const voltageL2Pu = typeof m.voltage_l2_pu === 'number' ? m.voltage_l2_pu : null;
      const voltageL3Pu = typeof m.voltage_l3_pu === 'number' ? m.voltage_l3_pu : null;
      const voltageUnbalancePercent = typeof m.voltage_unbalance_percent === 'number' ? m.voltage_unbalance_percent : null;

      // Extract three-phase power data
      const powerL1Kw = typeof m.power_l1_kw === 'number' ? m.power_l1_kw : null;
      const powerL2Kw = typeof m.power_l2_kw === 'number' ? m.power_l2_kw : null;
      const powerL3Kw = typeof m.power_l3_kw === 'number' ? m.power_l3_kw : null;

      // Extract three-phase current data from backend
      const currentL1A = typeof m.current_l1_a === 'number' ? m.current_l1_a : null;
      const currentL2A = typeof m.current_l2_a === 'number' ? m.current_l2_a : null;
      const currentL3A = typeof m.current_l3_a === 'number' ? m.current_l3_a : null;

      // Calculate per-phase voltages in V (from p.u. using 400/sqrt(3) nominal L-N)
      const voltageL1V = voltageL1Pu !== null ? voltageL1Pu * PU_BASE_LN_V : null;
      const voltageL2V = voltageL2Pu !== null ? voltageL2Pu * PU_BASE_LN_V : null;
      const voltageL3V = voltageL3Pu !== null ? voltageL3Pu * PU_BASE_LN_V : null;

      // Calculate phase values with fallbacks
      const phaseVoltageL1 = voltageL1V ?? voltageV ?? PU_BASE_LN_V;
      const phaseVoltageL2 = voltageL2V ?? voltageV ?? PU_BASE_LN_V;
      const phaseVoltageL3 = voltageL3V ?? voltageV ?? PU_BASE_LN_V;

      const phasePowerL1 = powerL1Kw ?? measurementValue / 3;
      const phasePowerL2 = powerL2Kw ?? measurementValue / 3;
      const phasePowerL3 = powerL3Kw ?? measurementValue / 3;

      // Use backend-provided currents, fallback to calculation if not available
      // Fallback calculation: I = P / (V × cos(φ))
      const defaultPowerFactor = 0.95;
      const currentL1 = currentL1A ?? (phaseVoltageL1 > 0 ? (Math.abs(phasePowerL1) * 1000) / (phaseVoltageL1 * defaultPowerFactor) : 0);
      const currentL2 = currentL2A ?? (phaseVoltageL2 > 0 ? (Math.abs(phasePowerL2) * 1000) / (phaseVoltageL2 * defaultPowerFactor) : 0);
      const currentL3 = currentL3A ?? (phaseVoltageL3 > 0 ? (Math.abs(phasePowerL3) * 1000) / (phaseVoltageL3 * defaultPowerFactor) : 0);

      mapped[m.element_id] = {
        totalActivePower: measurementValue,
        totalApparentPowerKva: typeof m.power_kva === 'number' ? m.power_kva : null,
        totalReactivePowerKvar: typeof m.power_kvar === 'number' ? m.power_kvar : null,
        timestamp: parseUtcTimestampMs(m.timestamp),
        simValue: m.simulated ?? null,
        resultValue: m.element_type === 'hausanschluss' ? null : measurementValue,
        phases: {
          L1: { voltage: phaseVoltageL1, current: currentL1, power: phasePowerL1 },
          L2: { voltage: phaseVoltageL2, current: currentL2, power: phasePowerL2 },
          L3: { voltage: phaseVoltageL3, current: currentL3, power: phasePowerL3 }
        },
        voltageKV: voltageKv,
        voltagePU: voltagePu,
        measuredVoltageV,
        measuredVoltagePu,
        measuredVoltageReference,
        measuredVoltageSource,
        measuredVoltageTimestamp,
        // Three-phase voltage data
        voltageL1Pu,
        voltageL2Pu,
        voltageL3Pu,
        voltageUnbalancePercent,
        loadingPercent: m.loading_percent ?? null,
        // Raw per-phase solver values (no fallback — see store type)
        powerL1Kw,
        powerL2Kw,
        powerL3Kw,
        currentL1A,
        currentL2A,
        currentL3A,
        inputSource: parseLoadInputSource(m.input_source)
      };
    }
  }

  store.updateSegments(segmentUpdates);
  store.updatePowerMeasurements(mapped);

  // Trennstellen: the coupling cable's figures come from the owner zone's result (NETZVERBUND.md 5.3).
  // An archive frame carries none, so the store empties and the map says so instead of keeping
  // stale live values next to a historical frame.
  useCouplingStore.getState().setFlows(Array.isArray(data.couplingFlows) ? data.couplingFlows : []);

  // Apply ring circuit data to Abgangsleitung elements. This must also run when
  // ringGroups is empty/absent — an operationally opened ring stops being reported
  // and its highlighting has to be cleared, not kept forever.
  {
    // Build lookup: elementId → ringId (covers both cable and segment IDs)
    const ringLookup = new Map<string, string>();
    for (const group of data.ringGroups ?? []) {
      for (const eid of group.elementIds) {
        ringLookup.set(eid, group.ringId);
      }
    }

    let ringDataChanged = false;
    const updatedElements = store.elements.map(el => {
      if (el.type !== 'abgangsleitung') return el;
      const cable = el as import('../types/network').Abgangsleitung;

      // Check cable ID, then segment IDs
      let ringId = ringLookup.get(cable.id);
      if (!ringId) {
        for (const seg of cable.segments) {
          ringId = ringLookup.get(seg.id);
          if (ringId) break;
        }
      }

      // Build a NEW object on change — memoized line components compare element
      // fields by reference, so in-place mutation would not repaint
      if (ringId) {
        if (!cable.isRingCircuit || cable.ringCircuitId !== ringId) {
          ringDataChanged = true;
          return { ...cable, isRingCircuit: true, ringCircuitId: ringId };
        }
      } else if (cable.isRingCircuit) {
        ringDataChanged = true;
        return { ...cable, isRingCircuit: false, ringCircuitId: undefined };
      }
      return el;
    });

    if (ringDataChanged) {
      store.setElements(updatedElements);
    }
  }
}
